import React, { useEffect, useState, useRef } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData, SearchBox } from 'components/commonparts'
import { DataList } from 'components/parts/MasterSupplier';
import { useCenter } from 'hooks/useCenter';
import { useGridSearch } from 'hooks/useGridSearch';
import { useSupplier } from 'hooks/useSupplier';
import { useTypecode } from 'hooks/useTypecode';

import type { GetMstSuppliersSupplierGetRequest, MstSupplier, ResponseResult } from 'api-client';

function MasterSupplier() {
  const { loginUserInfo } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  // const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  // const searchParamState = useSearchParamStore((state) => state.searchParamState)
  // const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
  // const localOrgSupplierId = localSearchParam?.item.orgSupplierId || ''

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstSuppliersSupplierGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstSupplier[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)

  const { supplierSearchResult, supplierSearch ,fetchSupplierSearch, supplierSearchIsLoading } = useSupplier()
  const { typeCode, fetchTypeCode } = useTypecode()
  const { centerSearchResult, centerSearch, fetchCenterSearch, isLoading: centerIsLoading } = useCenter()
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })
  // const [searchParam, setSearchParam] = useState<GetMstSuppliersSupplierGetRequest>(initSearchParam)
  const isFirstRender = useRef(true);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  useEffect(() => {
    setResultMessage(supplierSearchResult)
  }, [supplierSearchResult])
  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])

  // const doSearch = useCallback(async (requestParam: GetMstSuppliersSupplierGetRequest) => {
  //   if (hasGridEdited) {
  //     setDiscardChangeDialogOpen(true)
  //     return
  //   }
  //   setSearchTrigger((prev) => !prev);
  //   try {
  //     fetchSupplierSearch({
  //         ...requestParam,
  //       });
  //       // fetchSupplier({
  //       //   companyId: requestParam.companyId, 
  //       // });
  //       if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
  //         setSearchParamState<GetMstSuppliersSupplierGetRequest>(
  //           loginUserInfo?.tenantId,
  //           loginUserInfo?.companyId,
  //           'Supplier',
  //           {
  //             ...requestParam,
  //           }
  //         );
  //       }
  
  //       setSearchParam(requestParam);
  //       setHasGridEdited(false)
  //     } catch (error) {
  //       console.error("Error fetching data:", error);
  //     } 
  // }, [supplierSearch]);

  const handleRegister = () => {
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };

  const onReload = () => {
    fetchSupplierSearch({
      enabled: false,
      ...initSearchParam,
    }, true)
    setHasGridEdited(false)
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current = false
        setIsInitialLoading(true)
        const itemsPromise = fetchSupplierSearch({
          enabled: false,
          ...initSearchParam,
        }, true);
        const centerPromise = fetchCenterSearch({
          companyId: initSearchParam.companyId,
          enabled: false,
        }, true);
        await Promise.all([itemsPromise, centerPromise]);
      }
    }
    firstTimeRender()
  }, [])

  useEffect(() => {
    // Only check after the first API call has been made (not the initial render)
    if (!isFirstRender.current && isInitialLoading) {
      // Check if both API calls have completed and data is available
      const supplierReady = !supplierSearchIsLoading && supplierSearchResult !== undefined
      const centerReady = !centerIsLoading && centerSearchResult !== undefined
      
      if (supplierReady && centerReady) {
        setIsInitialLoading(false)
      }
    }
  }, [supplierSearchIsLoading, supplierSearchResult, centerIsLoading, centerSearchResult, isInitialLoading])

  const generateDownloadData = (): MstSupplier[] => {
    if (!supplierSearch?.list || supplierSearch.list.length === 0) {
      return [{} as MstSupplier]
    }
    const downloadData: MstSupplier[] = [...supplierSearch.list]
    return downloadData
  }

  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }

  return (
    <Box>
      <MasterSearchFrom
          result={resultMessage}
          onDoUpdate={handleRegister}
          onReload={onReload}
          onImportData={handleUploadData}
          onFileDownLoad={handleFileDownload}
          discardChangeDialogOpen={discardChangeDialogOpen}
          setDownloadMode={setDownloadMode}
          hasDataEdited={hasGridEdited}
          importRequiredFields={["org_supplier_id"]}
          headerJp={["仕入先ID"]}
          lastUpdateDatetime={supplierSearch?.lastUpdateDatetime}
      />
      <FileDownloadData fileName="仕入先マスタ" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      {!isInitialLoading && (
        <DataList
            list={supplierSearch}
            systype={typeCode}
            center={centerSearch?.list?.filter(c => c.internalTransferHubFlg === true && c.deleteFlg === false) || []}
            onReload={onReload}
            isLoading={supplierSearchIsLoading}
            searchParam={initSearchParam}
            onUploadData={uploadData}
            updateTrigger={updateTrigger}
            setHasGridEdited={setHasGridEdited}
            setResultMessage={setGridMessage}
            setDownloadColumnDefs={setDownloadColDef}
            gridRef={gridSearch.gridRef}
            findSearchValue={gridSearch.gridSearchProps.findSearchValue}
            onFindChanged={gridSearch.gridSearchProps.onFindChanged}
            isSearchVisible={gridSearch.searchBoxProps.isVisible}
          />
      )}
      
    </Box>
          
  )
}

export default MasterSupplier;
